import { workMatchesExpectedAnswer } from "./practiceAlgebra";
import {
  getPracticeGeneratorMeta,
  PRACTICE_GENERATOR_CATALOG,
  type PracticeGeneratorMeta,
} from "./practiceGeneratorCatalog";
import {
  decimalNumberLineFigure,
  decimalNumberLineSteps,
  fractionAddBarsFigure,
  fractionAddBarsSteps,
  percentBarFigure,
  percentBarSteps,
  type PracticeFigureStep,
} from "./practiceArithmeticFigures";
import { HIGH_SCHOOL_SAMPLERS } from "./practiceHighSchoolGenerators";
import { GEOMETRY_STATISTICS_SAMPLERS } from "./practiceGeometryStatisticsGenerators";
import type { LessonTemplateNotation } from "./lessonTemplates";
import type { PracticeLocale } from "./practiceLocale";
import type { PracticeDifficulty } from "./practiceQuestionTemplates";

export type PracticeDraw = {
  prompt: string;
  expectedAnswer: string;
  acceptedAnswers: string[];
  hints: string[];
  /**
   * Step-by-step solution of this draw. Shown only as a worked example
   * after a step-down, never as a hint and never on the public instance.
   */
  explanation?: string;
  /** Board marks for this draw. Labels use the same numbers as the prompt. */
  figure?: LessonTemplateNotation[];
  /** Playable groups of those marks. Captions never state the answer. */
  figureSteps?: PracticeFigureStep[];
};

export type PracticeInstance = {
  id: string;
  generatorId: string;
  skillId: string;
  grade: number;
  difficulty: PracticeDifficulty;
  seed: number;
  locale: PracticeLocale;
  name: string;
  prompt: string;
  hints: string[];
  subject: PracticeGeneratorMeta["subject"];
  band: PracticeGeneratorMeta["band"];
  expectedAnswer: string;
  acceptedAnswers: string[];
  /** Step-by-step solution. Omitted from the public payload. */
  explanation?: string;
  createdAt: string;
};

/** Sent to the browser. The verified answer stays on the server. */
export type PracticeInstancePublic = {
  id: string;
  generatorId: string;
  skillId: string;
  grade: number;
  difficulty: PracticeDifficulty;
  seed: number;
  locale: PracticeLocale;
  name: string;
  prompt: string;
  hints: string[];
  subject: PracticeGeneratorMeta["subject"];
  band: PracticeGeneratorMeta["band"];
};

export type GeneratedCheck = {
  correct: boolean;
  feedback: string;
  hint?: string;
  partComplete: boolean;
};

type Sampler = (rng: () => number, locale: PracticeLocale) => PracticeDraw;

/** mulberry32. Same seed always yields the same sequence. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function randInt(rng: () => number, min: number, max: number): number {
  const lo = Math.ceil(min);
  const hi = Math.floor(max);
  return lo + Math.floor(rng() * (hi - lo + 1));
}

function choice<T>(rng: () => number, items: readonly T[]): T {
  return items[randInt(rng, 0, items.length - 1)];
}

function text(locale: PracticeLocale, en: string, he: string): string {
  return locale === "he" ? he : en;
}

function hint(locale: PracticeLocale, en: string, he: string): string[] {
  return [text(locale, en, he)];
}

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

function fractionAnswer(
  num: number,
  den: number,
): { expectedAnswer: string; acceptedAnswers: string[] } {
  const g = gcd(num, den);
  const simpleNum = num / g;
  const simpleDen = den / g;
  const simple = `${simpleNum}/${simpleDen}`;
  const raw = `${num}/${den}`;
  const accepted = new Set<string>();
  if (raw !== simple) accepted.add(raw);
  if (simpleNum === simpleDen) accepted.add(String(simpleNum));
  else if (simpleDen === 1) accepted.add(String(simpleNum));
  return { expectedAnswer: simple, acceptedAnswers: [...accepted] };
}

function decimalText(tenths: number): { expectedAnswer: string; acceptedAnswers: string[] } {
  const whole = Math.floor(tenths / 10);
  const frac = tenths % 10;
  const expectedAnswer = `${whole}.${frac}`;
  const acceptedAnswers = frac === 0 ? [String(whole)] : [];
  return { expectedAnswer, acceptedAnswers };
}

function pickOther(
  rng: () => number,
  min: number,
  max: number,
  exclude: number,
): number {
  const pool: number[] = [];
  for (let n = min; n <= max; n += 1) {
    if (n !== exclude) pool.push(n);
  }
  return choice(rng, pool);
}

const SAMPLERS: Record<string, Sampler> = {
  ...HIGH_SCHOOL_SAMPLERS,
  ...GEOMETRY_STATISTICS_SAMPLERS,
  "arithmetic.count-on"(rng, locale) {
    const n = randInt(rng, 0, 9);
    return {
      prompt: text(
        locale,
        `What number comes after ${n}?`,
        `איזה מספר בא אחרי ${n}?`,
      ),
      expectedAnswer: String(n + 1),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Say the next counting number.",
        "אמרו את המספר הבא בספירה.",
      ),
    };
  },

  "arithmetic.add-within-5"(rng, locale) {
    const a = randInt(rng, 0, 5);
    const b = randInt(rng, 0, 5 - a);
    const left = a + b === 0 ? 1 : a;
    const right = a + b === 0 ? 1 : b;
    return {
      prompt: text(locale, `Add. ${left} + ${right} =`, `חברו. ${left} + ${right} =`),
      expectedAnswer: String(left + right),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Start at the first number and count on.",
        "התחילו מהמספר הראשון וספרו הלאה.",
      ),
    };
  },

  "arithmetic.add-within-10"(rng, locale) {
    const sum = randInt(rng, 6, 10);
    const a = randInt(rng, 1, sum - 1);
    const b = sum - a;
    return {
      prompt: text(locale, `Add. ${a} + ${b} =`, `חברו. ${a} + ${b} =`),
      expectedAnswer: String(sum),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Count on from the larger addend.",
        "ספרו הלאה מהמחובר הגדול יותר.",
      ),
    };
  },

  "arithmetic.add-two-digit-no-carry"(rng, locale) {
    const aT = randInt(rng, 1, 9);
    const bT = randInt(rng, 1, 9);
    const aO = randInt(rng, 0, 9);
    const bO = randInt(rng, 0, 9 - aO);
    const a = aT * 10 + aO;
    const b = bT * 10 + bO;
    return {
      prompt: text(locale, `Add. ${a} + ${b} =`, `חברו. ${a} + ${b} =`),
      expectedAnswer: String(a + b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Add the ones, then the tens. The ones stay a single digit.",
        "חברו את האחדות, ואז את העשרות. האחדות נשארות ספרה אחת.",
      ),
    };
  },

  "arithmetic.add-carry"(rng, locale) {
    const aT = randInt(rng, 1, 9);
    const bT = randInt(rng, 1, 9);
    const aO = randInt(rng, 1, 9);
    const bO = randInt(rng, Math.max(1, 10 - aO), 9);
    const a = aT * 10 + aO;
    const b = bT * 10 + bO;
    return {
      prompt: text(locale, `Add. ${a} + ${b} =`, `חברו. ${a} + ${b} =`),
      expectedAnswer: String(a + b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Add the ones. If they make a ten, write the leftover ones and carry a ten.",
        "חברו את האחדות. אם נוצרת עשרת, כתבו את האחדות שנשארו והעבירו עשרת.",
      ),
    };
  },

  "arithmetic.add-carry-hundreds"(rng, locale) {
    const aH = randInt(rng, 1, 7);
    const bH = randInt(rng, 1, 7);
    const aT = randInt(rng, 0, 8);
    const bT = randInt(rng, 0, 8);
    const aO = randInt(rng, 5, 9);
    const bO = randInt(rng, 5, 9);
    const a = aH * 100 + aT * 10 + aO;
    const b = bH * 100 + bT * 10 + bO;
    return {
      prompt: text(locale, `Add. ${a} + ${b} =`, `חברו. ${a} + ${b} =`),
      expectedAnswer: String(a + b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Add ones, then tens, then hundreds. Carry when a column makes a ten.",
        "חברו אחדות, אחר כך עשרות, ואחר כך מאות. העבירו כשבעמודה נוצרת עשרת.",
      ),
    };
  },

  "arithmetic.sub-within-10"(rng, locale) {
    const a = randInt(rng, 1, 10);
    const b = randInt(rng, 0, a);
    return {
      prompt: text(locale, `Subtract. ${a} − ${b} =`, `חסרו. ${a} − ${b} =`),
      expectedAnswer: String(a - b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Count back from the first number.",
        "ספרו אחורה מהמספר הראשון.",
      ),
    };
  },

  "arithmetic.sub-two-digit-no-borrow"(rng, locale) {
    const aT = randInt(rng, 2, 9);
    const bT = randInt(rng, 1, aT - 1);
    const aO = randInt(rng, 0, 9);
    const bO = randInt(rng, 0, aO);
    const a = aT * 10 + aO;
    const b = bT * 10 + bO;
    return {
      prompt: text(locale, `Subtract. ${a} − ${b} =`, `חסרו. ${a} − ${b} =`),
      expectedAnswer: String(a - b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Subtract the ones, then the tens. No regrouping is needed.",
        "חסרו את האחדות, ואז את העשרות. אין צורך לפרק.",
      ),
    };
  },

  "arithmetic.sub-borrow"(rng, locale) {
    const aT = randInt(rng, 2, 9);
    const bT = randInt(rng, 1, aT - 1);
    const aO = randInt(rng, 0, 8);
    const bO = randInt(rng, aO + 1, 9);
    const a = aT * 10 + aO;
    const b = bT * 10 + bO;
    return {
      prompt: text(locale, `Subtract. ${a} − ${b} =`, `חסרו. ${a} − ${b} =`),
      expectedAnswer: String(a - b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Regroup one ten into ones, then subtract.",
        "פרקו עשרת אחת לאחדות, ואז חסרו.",
      ),
    };
  },

  "arithmetic.sub-borrow-hundreds"(rng, locale) {
    const aH = randInt(rng, 2, 9);
    const bH = randInt(rng, 1, aH - 1);
    const aT = randInt(rng, 0, 8);
    const bT = randInt(rng, aT + 1, 9);
    const aO = randInt(rng, 0, 9);
    const bO = randInt(rng, 0, 9);
    const a = aH * 100 + aT * 10 + aO;
    const b = bH * 100 + bT * 10 + bO;
    return {
      prompt: text(locale, `Subtract. ${a} − ${b} =`, `חסרו. ${a} − ${b} =`),
      expectedAnswer: String(a - b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Regroup from the next column when the top digit is smaller.",
        "פרקו מהעמודה הבאה כשהספרה העליונה קטנה יותר.",
      ),
    };
  },

  "arithmetic.place-tens"(rng, locale) {
    const tens = randInt(rng, 1, 9);
    const ones = pickOther(rng, 0, 9, tens);
    const n = tens * 10 + ones;
    return {
      prompt: text(
        locale,
        `In ${n}, what is the value of the digit ${tens}?`,
        `במספר ${n}, מה הערך של הספרה ${tens}?`,
      ),
      expectedAnswer: String(tens * 10),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "The tens digit tells how many tens. Write that value.",
        "ספרת העשרות אומרת כמה עשרות. כתבו את הערך.",
      ),
    };
  },

  "arithmetic.place-hundreds"(rng, locale) {
    const hundreds = randInt(rng, 1, 9);
    const tens = pickOther(rng, 0, 9, hundreds);
    const ones = pickOther(rng, 0, 9, hundreds);
    const n = hundreds * 100 + tens * 10 + ones;
    return {
      prompt: text(
        locale,
        `In ${n}, what is the value of the digit ${hundreds}?`,
        `במספר ${n}, מה הערך של הספרה ${hundreds}?`,
      ),
      expectedAnswer: String(hundreds * 100),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "The hundreds digit tells how many hundreds. Write that value.",
        "ספרת המאות אומרת כמה מאות. כתבו את הערך.",
      ),
    };
  },

  "arithmetic.multiply-easy"(rng, locale) {
    const a = choice(rng, [2, 5, 10]);
    const b = randInt(rng, 1, 10);
    return {
      prompt: text(locale, `Multiply. ${a} × ${b} =`, `כפלו. ${a} × ${b} =`),
      expectedAnswer: String(a * b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Skip-count by one of the factors.",
        "ספרו בדילוגים לפי אחד הגורמים.",
      ),
    };
  },

  "arithmetic.multiply-core"(rng, locale) {
    const a = choice(rng, [3, 4, 6]);
    const b = randInt(rng, 1, 10);
    return {
      prompt: text(locale, `Multiply. ${a} × ${b} =`, `כפלו. ${a} × ${b} =`),
      expectedAnswer: String(a * b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Skip-count by one of the factors.",
        "ספרו בדילוגים לפי אחד הגורמים.",
      ),
    };
  },

  "arithmetic.multiply-hard"(rng, locale) {
    const a = choice(rng, [7, 8, 9]);
    const b = randInt(rng, 1, 10);
    return {
      prompt: text(locale, `Multiply. ${a} × ${b} =`, `כפלו. ${a} × ${b} =`),
      expectedAnswer: String(a * b),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Skip-count by one of the factors.",
        "ספרו בדילוגים לפי אחד הגורמים.",
      ),
    };
  },

  "arithmetic.divide-exact"(rng, locale) {
    const divisor = choice(rng, [2, 5, 10]);
    const quotient = randInt(rng, 1, 10);
    const dividend = divisor * quotient;
    return {
      prompt: text(
        locale,
        `Divide. ${dividend} ÷ ${divisor} =`,
        `חלקו. ${dividend} ÷ ${divisor} =`,
      ),
      expectedAnswer: String(quotient),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Find the factor that multiplies the divisor to make the dividend.",
        "מצאו את הגורם שכפול המחלק נותן את המחולק.",
      ),
    };
  },

  "arithmetic.divide-exact-core"(rng, locale) {
    const divisor = randInt(rng, 3, 9);
    const quotient = randInt(rng, 1, 10);
    const dividend = divisor * quotient;
    return {
      prompt: text(
        locale,
        `Divide. ${dividend} ÷ ${divisor} =`,
        `חלקו. ${dividend} ÷ ${divisor} =`,
      ),
      expectedAnswer: String(quotient),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Find the factor that multiplies the divisor to make the dividend.",
        "מצאו את הגורם שכפול המחלק נותן את המחולק.",
      ),
    };
  },

  "arithmetic.divide-remainder"(rng, locale) {
    const divisor = randInt(rng, 2, 9);
    const quotient = randInt(rng, 1, 9);
    const remainder = randInt(rng, 1, divisor - 1);
    const dividend = quotient * divisor + remainder;
    const expectedAnswer = `${quotient} r ${remainder}`;
    return {
      prompt: text(
        locale,
        `Divide. ${dividend} ÷ ${divisor} =`,
        `חלקו. ${dividend} ÷ ${divisor} =`,
      ),
      expectedAnswer,
      acceptedAnswers: [
        `${quotient} R ${remainder}`,
        `${quotient}r${remainder}`,
      ],
      hints: hint(
        locale,
        "Divide as far as you can. Write the leftover after the letter r.",
        "חלקו עד כמה שאפשר. כתבו את השארית אחרי האות r.",
      ),
    };
  },

  "arithmetic.compare-fractions-same-den"(rng, locale) {
    const den = randInt(rng, 3, 9);
    const n1 = randInt(rng, 1, den - 1);
    const n2 = pickOther(rng, 1, den - 1, n1);
    const greater = Math.max(n1, n2);
    return {
      prompt: text(
        locale,
        `Which fraction is greater? ${n1}/${den} or ${n2}/${den}`,
        `איזה שבר גדול יותר? ${n1}/${den} או ${n2}/${den}`,
      ),
      expectedAnswer: `${greater}/${den}`,
      acceptedAnswers: [],
      hints: hint(
        locale,
        "The denominators match, so the larger numerator is greater.",
        "המכנים שווים, ולכן המונה הגדול יותר הוא השבר הגדול.",
      ),
    };
  },

  "arithmetic.compare-fractions-same-num"(rng, locale) {
    const numerator = randInt(rng, 1, 4);
    const d1 = randInt(rng, numerator + 1, 8);
    const d2 = pickOther(rng, numerator + 1, 9, d1);
    const smallerDen = Math.min(d1, d2);
    return {
      prompt: text(
        locale,
        `Which fraction is greater? ${numerator}/${d1} or ${numerator}/${d2}`,
        `איזה שבר גדול יותר? ${numerator}/${d1} או ${numerator}/${d2}`,
      ),
      expectedAnswer: `${numerator}/${smallerDen}`,
      acceptedAnswers: [],
      hints: hint(
        locale,
        "The numerators match. The smaller denominator is the larger share.",
        "המונים שווים. המכנה הקטן הוא החלק הגדול יותר.",
      ),
    };
  },

  "arithmetic.add-fractions-same-den"(rng, locale) {
    const den = randInt(rng, 2, 8);
    const n1 = randInt(rng, 1, den - 1);
    const n2 = randInt(rng, 1, den - 1);
    const answer = fractionAnswer(n1 + n2, den);
    return {
      prompt: text(
        locale,
        `Add. ${n1}/${den} + ${n2}/${den} =`,
        `חברו. ${n1}/${den} + ${n2}/${den} =`,
      ),
      expectedAnswer: answer.expectedAnswer,
      acceptedAnswers: answer.acceptedAnswers,
      hints: hint(
        locale,
        "Add the numerators and keep the denominator. Simplify if you can.",
        "חברו את המונים והשאירו את המכנה. צמצמו אם אפשר.",
      ),
      figure: fractionAddBarsFigure(n1, n2, den),
      figureSteps: fractionAddBarsSteps(n1, n2, den),
    };
  },

  "arithmetic.add-fractions-related"(rng, locale) {
    const pairs: ReadonlyArray<readonly [number, number]> = [
      [2, 4],
      [2, 6],
      [3, 6],
      [4, 8],
      [2, 8],
      [3, 9],
    ];
    const [d1, d2] = choice(rng, pairs);
    const n1 = randInt(rng, 1, d1 - 1);
    const n2 = randInt(rng, 1, d2 - 1);
    const factor = d2 / d1;
    const answer = fractionAnswer(n1 * factor + n2, d2);
    return {
      prompt: text(
        locale,
        `Add. ${n1}/${d1} + ${n2}/${d2} =`,
        `חברו. ${n1}/${d1} + ${n2}/${d2} =`,
      ),
      expectedAnswer: answer.expectedAnswer,
      acceptedAnswers: answer.acceptedAnswers,
      hints: hint(
        locale,
        "Rewrite one fraction so the denominators match, then add the numerators.",
        "כתבו שבר אחד עם אותו מכנה, ואז חברו את המונים.",
      ),
    };
  },

  "arithmetic.equivalent-fractions"(rng, locale) {
    const den = randInt(rng, 2, 6);
    const num = randInt(rng, 1, den - 1);
    const k = randInt(rng, 2, 4);
    return {
      prompt: text(
        locale,
        `Multiply the numerator and the denominator of ${num}/${den} by ${k}.`,
        `כפלו את המונה ואת המכנה של ${num}/${den} ב-${k}.`,
      ),
      expectedAnswer: `${num * k}/${den * k}`,
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Multiply the numerator and the denominator by the same number.",
        "כפלו את המונה ואת המכנה באותו מספר.",
      ),
    };
  },

  "arithmetic.equivalent-missing"(rng, locale) {
    const den = randInt(rng, 2, 6);
    const num = randInt(rng, 1, den - 1);
    const k = randInt(rng, 2, 4);
    const missing = num * k;
    const full = `${missing}/${den * k}`;
    return {
      prompt: text(
        locale,
        `Fill the missing numerator. ${num}/${den} = ?/${den * k}`,
        `השלימו את המונה החסר. ${num}/${den} = ?/${den * k}`,
      ),
      expectedAnswer: String(missing),
      acceptedAnswers: [full],
      hints: hint(
        locale,
        "The denominator grew by a factor. Grow the numerator by that same factor.",
        "המכנה גדל בגורם. הגדילו את המונה באותו גורם.",
      ),
    };
  },

  "arithmetic.decimals-compare"(rng, locale) {
    const a = randInt(rng, 1, 9);
    const b = pickOther(rng, 1, 9, a);
    const greater = Math.max(a, b);
    const left = `0.${a}`;
    const right = `0.${b}`;
    return {
      prompt: text(
        locale,
        `Which decimal is greater? ${left} or ${right}`,
        `איזה מספר עשרוני גדול יותר? ${left} או ${right}`,
      ),
      expectedAnswer: `0.${greater}`,
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Compare the tenths place. The larger digit is the greater decimal.",
        "השוו את ספרת העשיריות. הספרה הגדולה יותר היא המספר הגדול.",
      ),
      figure: decimalNumberLineFigure([a, b]),
      figureSteps: decimalNumberLineSteps([a, b]),
    };
  },

  "arithmetic.decimals-add"(rng, locale) {
    const a = randInt(rng, 1, 9);
    const b = randInt(rng, 1, 9);
    const answer = decimalText(a + b);
    return {
      prompt: text(
        locale,
        `Add. 0.${a} + 0.${b} =`,
        `חברו. 0.${a} + 0.${b} =`,
      ),
      expectedAnswer: answer.expectedAnswer,
      acceptedAnswers: answer.acceptedAnswers,
      hints: hint(
        locale,
        "Line up the decimal points and add the tenths.",
        "יישרו את הנקודות העשרוניות וחברו את העשיריות.",
      ),
      figure: decimalNumberLineFigure([a, b]),
      figureSteps: decimalNumberLineSteps([a, b]),
    };
  },

  "arithmetic.percent-simple"(rng, locale) {
    const n = randInt(rng, 1, 10) * 2;
    return {
      prompt: text(
        locale,
        `What is 50% of ${n}?`,
        `כמה הם 50% מתוך ${n}?`,
      ),
      expectedAnswer: String(n / 2),
      acceptedAnswers: [],
      hints: hint(locale, "Take half of the number.", "קחו חצי מהמספר."),
      figure: percentBarFigure(2, n, 50),
      figureSteps: percentBarSteps(2, n, 50),
    };
  },

  "arithmetic.percent-simple-core"(rng, locale) {
    const tenPercent = rng() < 0.5;
    if (tenPercent) {
      const n = randInt(rng, 1, 10) * 10;
      return {
        prompt: text(
          locale,
          `What is 10% of ${n}?`,
          `כמה הם 10% מתוך ${n}?`,
        ),
        expectedAnswer: String(n / 10),
        acceptedAnswers: [],
        hints: hint(
          locale,
          "Take one tenth of the number.",
          "קחו עשירית מהמספר.",
        ),
        figure: percentBarFigure(10, n, 10),
        figureSteps: percentBarSteps(10, n, 10),
      };
    }
    const n = randInt(rng, 1, 10) * 4;
    return {
      prompt: text(
        locale,
        `What is 25% of ${n}?`,
        `כמה הם 25% מתוך ${n}?`,
      ),
      expectedAnswer: String(n / 4),
      acceptedAnswers: [],
      hints: hint(
        locale,
        "Take one quarter of the number.",
        "קחו רבע מהמספר.",
      ),
      figure: percentBarFigure(4, n, 25),
      figureSteps: percentBarSteps(4, n, 25),
    };
  },
};

export function practiceSamplerIds(): string[] {
  return Object.keys(SAMPLERS);
}

/** The figure for a minted exercise. Recomputed from the seed so it is not stored. */
export function practiceGeneratedFigure(
  generatorId: string,
  seed: number,
  locale: PracticeLocale,
): LessonTemplateNotation[] {
  const sample = SAMPLERS[generatorId];
  if (!sample) return [];
  return sample(mulberry32(seed >>> 0), locale).figure ?? [];
}

/** Playable groups for a minted exercise. Empty when the figure is a single dump. */
export function practiceGeneratedFigureSteps(
  generatorId: string,
  seed: number,
  locale: PracticeLocale,
): PracticeFigureStep[] {
  const sample = SAMPLERS[generatorId];
  if (!sample) return [];
  return sample(mulberry32(seed >>> 0), locale).figureSteps ?? [];
}

function assertHintsSafe(draw: PracticeDraw): void {
  if (!draw.hints.length || draw.hints.some((line) => !line.trim())) {
    throw new Error("generator hint is empty");
  }
  const banned = [draw.expectedAnswer, ...draw.acceptedAnswers].filter(Boolean);
  for (const line of draw.hints) {
    if (/\d/.test(line)) {
      throw new Error("generator hint contains a digit");
    }
    for (const answer of banned) {
      if (line.includes(answer)) {
        throw new Error("generator hint contains the answer");
      }
    }
  }
}

/** The solve result must match, and a shifted answer must not. */
export function assertInstanceVerified(instance: {
  expectedAnswer: string;
  acceptedAnswers: string[];
}): void {
  const { expectedAnswer, acceptedAnswers } = instance;
  if (!expectedAnswer.trim()) {
    throw new Error("generator answer is empty");
  }
  if (
    !workMatchesExpectedAnswer(expectedAnswer, expectedAnswer, acceptedAnswers)
  ) {
    throw new Error("self-check failed: expected answer does not verify");
  }
  for (const alt of acceptedAnswers) {
    if (!workMatchesExpectedAnswer(alt, expectedAnswer, acceptedAnswers)) {
      throw new Error("self-check failed: accepted answer does not verify");
    }
  }
  const shifted = `${expectedAnswer}9`;
  if (workMatchesExpectedAnswer(shifted, expectedAnswer, acceptedAnswers)) {
    throw new Error("self-check failed: shifted answer was accepted");
  }
}

export function mintPracticeInstance(opts: {
  id: string;
  generatorId: string;
  grade: number;
  locale: PracticeLocale;
  seed: number;
  now?: Date;
}): PracticeInstance {
  const meta = getPracticeGeneratorMeta(opts.generatorId);
  const sample = SAMPLERS[opts.generatorId];
  if (!meta || !sample) {
    throw new Error("practice generator not found");
  }
  if (opts.grade < meta.gradeMin || opts.grade > meta.gradeMax) {
    throw new Error("grade outside generator span");
  }
  const seed = opts.seed >>> 0;
  const draw = sample(mulberry32(seed), opts.locale);
  const instance: PracticeInstance = {
    id: opts.id,
    generatorId: meta.id,
    skillId: meta.skillId,
    grade: opts.grade,
    difficulty: meta.difficulty,
    seed,
    locale: opts.locale,
    name: meta.name[opts.locale],
    prompt: draw.prompt,
    hints: draw.hints,
    subject: meta.subject,
    band: meta.band,
    expectedAnswer: draw.expectedAnswer,
    acceptedAnswers: draw.acceptedAnswers,
    ...(draw.explanation ? { explanation: draw.explanation } : {}),
    createdAt: (opts.now ?? new Date()).toISOString(),
  };
  try {
    assertHintsSafe(draw);
    assertInstanceVerified(instance);
  } catch (error) {
    const message = error instanceof Error ? error.message : "self-check failed";
    throw new Error(`${opts.generatorId}: ${message}`);
  }
  return instance;
}

export function toPublicInstance(instance: PracticeInstance): PracticeInstancePublic {
  return {
    id: instance.id,
    generatorId: instance.generatorId,
    skillId: instance.skillId,
    grade: instance.grade,
    difficulty: instance.difficulty,
    seed: instance.seed,
    locale: instance.locale,
    name: instance.name,
    prompt: instance.prompt,
    hints: instance.hints.slice(),
    subject: instance.subject,
    band: instance.band,
  };
}

export function checkGeneratedWork(
  instance: Pick<PracticeInstance, "expectedAnswer" | "acceptedAnswers" | "hints">,
  studentWork: string,
): GeneratedCheck {
  const correct = workMatchesExpectedAnswer(
    studentWork,
    instance.expectedAnswer,
    instance.acceptedAnswers,
  );
  if (correct) {
    return {
      correct: true,
      feedback: "Correct — that matches the expected answer.",
      partComplete: true,
    };
  }
  return {
    correct: false,
    feedback: "Not quite — keep trying.",
    hint: instance.hints[0],
    partComplete: false,
  };
}

function mintRetry(opts: {
  id: string;
  generatorId: string;
  grade: number;
  locale: PracticeLocale;
  seed: number;
  now?: Date;
}): PracticeInstance {
  let lastError: unknown;
  for (let offset = 0; offset < 12; offset += 1) {
    try {
      return mintPracticeInstance({
        ...opts,
        seed: (opts.seed + offset) >>> 0,
      });
    } catch (error) {
      lastError = error;
    }
  }
  const message = lastError instanceof Error ? lastError.message : "mint failed";
  throw new Error(message);
}

/**
 * A graded instance, plus a different revealed example when the student
 * just stepped down. The example is not saved and is not the next problem.
 */
export function mintAdaptiveFollowUp(opts: {
  problemId: string;
  generatorId: string;
  grade: number;
  locale: PracticeLocale;
  seed: number;
  showWorkedExample: boolean;
  now?: Date;
}): {
  problem: PracticeInstance;
  example: {
    prompt: string;
    expectedAnswer: string;
    explanation?: string;
  } | null;
} {
  const problem = mintRetry({
    id: opts.problemId,
    generatorId: opts.generatorId,
    grade: opts.grade,
    locale: opts.locale,
    seed: opts.seed,
    now: opts.now,
  });
  if (!opts.showWorkedExample) return { problem, example: null };
  let example = mintRetry({
    id: `${opts.problemId}-example`,
    generatorId: opts.generatorId,
    grade: opts.grade,
    locale: opts.locale,
    seed: (opts.seed + 97) >>> 0,
    now: opts.now,
  });
  for (let offset = 0; offset < 16 && example.prompt === problem.prompt; offset += 1) {
    example = mintRetry({
      id: `${opts.problemId}-example`,
      generatorId: opts.generatorId,
      grade: opts.grade,
      locale: opts.locale,
      seed: (opts.seed + 200 + offset) >>> 0,
      now: opts.now,
    });
  }
  return {
    problem,
    example: {
      prompt: example.prompt,
      expectedAnswer: example.expectedAnswer,
      explanation: example.explanation,
    },
  };
}

export function catalogMatchesSamplers(): boolean {
  const ids = PRACTICE_GENERATOR_CATALOG.map((row) => row.id).sort();
  const samplers = Object.keys(SAMPLERS).sort();
  return ids.length === samplers.length && ids.every((id, i) => id === samplers[i]);
}
